import { eupasProperties, eupasDependencies } from "./eupasDataSchema"
import { ctisProperties, ctisDependencies } from "./ctisDataSchema"
import { ageLimits, getSecondaryIds } from "./commonSchemas"
import {
  STUDY_PHASES,
  ALLOCATION,
  INTERVENTIONAL_STUDY_MODEL,
  PRIMARY_PURPOSE,
  GENDER,
  DATE_TYPE,
  SECONDARY_ID_TYPES,
  CONTACT_TITLES,
  OBSERVATIONAL_STUDY_MODEL,
  SHARE_IPD,
  RECRUITMENT_STATUS_OPTIONS,
  ARM_TYPES,
  YES_NO_UNSELECTED,
  DATA_INFO,
} from "../enum/enum"

import {
  RECRUITMENT_STATUS_MAPPING,
  PRIMARY_PURPOSE_MAPPING,
  STUDY_PHASES_MAPPING,
  OBSERVATIONAL_STUDY_MODEL_MAPPING,
} from "../utils/registryOptionsMapping"

export const study_identification = {
  type: "object",
  properties: {
    brief_title: {
      title: "Brief Title",
      type: "string",
    },
    acronym: {
      type: "string",
      title: "Protocol ID",
      tooltip: "If specified, will be included at end of Brief Title in parentheses.",
    },
    official_title: {
      title: "Official Title",
      type: "string",
      placeholder: "Enter Official Title",
    },
    secondary_ids: getSecondaryIds("ctg"),
    ...eupasProperties.study_identification,
    ...ctisProperties.study_identification,
  },
  dependencies: {
    ...eupasDependencies.study_identification,
  },
}

export const study_oversight = {
  interventional: {
    title: "Oversight",
    type: "object",
    properties: {
      us_drug: {
        type: "object",
        properties: {
          us_fda_drug: {
            type: "string",
            enum: [],
            options: YES_NO_UNSELECTED,
          },
          productexported_from_us: {
            type: "string",
            enum: [],
            options: YES_NO_UNSELECTED,
          },
        },
      },
      us_device: {
        type: "object",
        properties: {
          us_fda_device: {
            type: "string",
            enum: [],
            options: YES_NO_UNSELECTED,
          },
          unapproved_uncleared_device: {
            type: "string",
            enum: [],
            options: YES_NO_UNSELECTED,
          },
          pediatric_postmarket_surveillance: {
            type: "string",
            enum: [],
            options: YES_NO_UNSELECTED,
          },
        },
        dependencies: {
          unapproved_uncleared_device: {
            oneOf: [
              {
                properties: {
                  unapproved_uncleared_device: {
                    enum: ["Yes"],
                    options: YES_NO_UNSELECTED,
                  },
                  post_prior_to_approval_clearance: {
                    type: "string",
                    enum: [],
                    options: YES_NO_UNSELECTED,
                  },
                },
              },
            ],
          },
        },
      },
      us_ind: {
        type: "object",
        properties: {
          us_fda_ind_ide: {
            type: "string",
            enum: [],
            options: YES_NO_UNSELECTED,
          },
        },
        dependencies: {
          us_fda_ind_ide: {
            oneOf: [
              {
                properties: {
                  us_fda_ind_ide: {
                    enum: ["Yes"],
                  },
                  fda_center: {
                    enum: ["Unselected", "CDER", "CBER", "CDRH"],
                  },
                  ind_ide_number: {
                    type: "string",
                  },
                  ind_serial_number: {
                    type: "string",
                  },
                  availability_of_expanded_access: {
                    type: "string",
                    enum: ["Unselected", "Yes", "No", "Unknown"],
                  },
                },
                dependencies: {
                  availability_of_expanded_access: {
                    oneOf: [
                      {
                        properties: {
                          availability_of_expanded_access: {
                            enum: ["Yes"],
                          },
                          expanded_access_record: {
                            type: "string",
                          },
                        },
                      },
                    ],
                  },
                },
              },
            ],
          },
        },
      },
      human_subjects_protection_review: {
        type: "object",
        properties: {
          board_status: {
            type: "string",
            enum: [
              "--Select--",
              "Request not yet submitted",
              "Submitted, pending",
              "Submitted, approved",
              "Exempt",
              "Submitted, denied",
              "Submission not required",
            ],
          },
        },
        dependencies: {
          board_status: {
            oneOf: [
              {
                properties: {
                  board_status: {
                    enum: ["Request not yet submitted", "Submitted, pending", "Submitted, denied"],
                  },
                  board_name: {
                    type: "string",
                  },
                  board_affiliation: {
                    type: "string",
                  },
                  board_contact: {
                    title: "Board Contact",
                    type: "object",
                    properties: {
                      phone: {
                        type: "string",
                      },
                      extension: {
                        type: "string",
                      },
                      email: {
                        type: "string",
                      },
                      address: {
                        type: "string",
                      },
                    },
                  },
                },
              },
              {
                properties: {
                  board_status: {
                    enum: ["Submitted, approved"],
                  },
                  approvalnumber: {
                    type: "string",
                  },
                  board_name: {
                    type: "string",
                  },
                  board_affiliation: {
                    type: "string",
                  },
                  board_contact: {
                    title: "Board Contact",
                    type: "object",
                    properties: {
                      phone: {
                        type: "string",
                      },
                      extension: {
                        type: "string",
                      },
                      email: {
                        type: "string",
                      },
                      address: {
                        type: "string",
                      },
                    },
                  },
                },
              },
            ],
          },
        },
      },
      data: {
        type: "object",
        properties: {
          data_monitoring_committee: {
            type: "string",
            enum: [],
            options: YES_NO_UNSELECTED,
          },
        },
      },
      fda: {
        type: "object",
        properties: {
          fda_regulated_intervention: {
            type: "string",
            enum: [],
            options: YES_NO_UNSELECTED,
          },
        },
        dependencies: {
          fda_regulated_intervention: {
            oneOf: [
              {
                properties: {
                  fda_regulated_intervention: {
                    enum: ["Yes"],
                  },
                  section_801_clinical_trial: {
                    type: "string",
                    enum: [],
                    options: YES_NO_UNSELECTED,
                  },
                },
              },
            ],
          },
        },
      },
    },
  },
  observational: {
    title: "Oversight",
    type: "object",
    properties: {
      us_drug: {
        type: "object",
        properties: {
          us_fda_drug: {
            type: "string",
            enum: [],
            options: YES_NO_UNSELECTED,
          },
          productexported_from_us: {
            type: "string",
            enum: [],
            options: YES_NO_UNSELECTED,
          },
        },
      },
      us_device: {
        type: "object",
        properties: {
          us_fda_device: {
            type: "string",
            enum: [],
            options: YES_NO_UNSELECTED,
          },
        },
        dependencies: {
          us_fda_device: {
            oneOf: [
              {
                properties: {
                  us_fda_device: {
                    enum: ["Yes"],
                  },
                  unapproved_uncleared_device: {
                    type: "string",
                    enum: [],
                    options: YES_NO_UNSELECTED,
                  },
                  pediatric_postmarket_surveillance: {
                    type: "string",
                    enum: [],
                    options: YES_NO_UNSELECTED,
                  },
                },
                dependencies: {
                  unapproved_uncleared_device: {
                    oneOf: [
                      {
                        properties: {
                          unapproved_uncleared_device: {
                            enum: ["Yes"],
                            options: YES_NO_UNSELECTED,
                          },
                          post_prior_to_approval_clearance: {
                            type: "string",
                            enum: [],
                            options: YES_NO_UNSELECTED,
                          },
                        },
                      },
                    ],
                  },
                },
              },
            ],
          },
        },
      },
      us_ind: {
        type: "object",
        properties: {
          us_fda_ind_ide: {
            type: "string",
            enum: [],
            options: YES_NO_UNSELECTED,
          },
        },
        dependencies: {
          us_fda_ind_ide: {
            oneOf: [
              {
                properties: {
                  us_fda_ind_ide: {
                    enum: ["Yes"],
                  },
                  fda_center: {
                    enum: ["CDER", "CBER", "CDRH"],
                  },
                  ind_ide_number: {
                    type: "string",
                  },
                  ind_serial_number: {
                    type: "string",
                  },
                  availability_of_expanded_access: {
                    type: "string",
                    enum: ["Yes", "No", "Unknown"],
                  },
                },
                dependencies: {
                  availability_of_expanded_access: {
                    oneOf: [
                      {
                        properties: {
                          availability_of_expanded_access: {
                            enum: ["Yes"],
                          },
                          expanded_access_record: {
                            type: "string",
                          },
                        },
                      },
                    ],
                  },
                },
              },
            ],
          },
        },
      },
      human_subjects_protection_review: {
        type: "object",
        properties: {
          board_status: {
            type: "string",
            enum: [
              "Request not yet submitted",
              "Submitted, pending",
              "Submitted, approved",
              "Exempt",
              "Submitted, denied",
              "Submission not required",
            ],
          },
        },
        dependencies: {
          board_status: {
            oneOf: [
              {
                properties: {
                  board_status: {
                    enum: ["Request not yet submitted", "Submitted, pending", "Submitted, denied"],
                  },
                  board_name: {
                    type: "string",
                  },
                  board_affiliation: {
                    type: "string",
                  },
                  board_contact: {
                    title: "Board Contact",
                    type: "object",
                    properties: {
                      phone: {
                        type: "string",
                      },
                      extension: {
                        type: "string",
                      },
                      email: {
                        type: "string",
                      },
                      address: {
                        type: "string",
                      },
                    },
                  },
                },
              },
              {
                properties: {
                  board_status: {
                    enum: ["Submitted, approved"],
                  },
                  approvalnumber: {
                    type: "string",
                  },
                  board_name: {
                    type: "string",
                  },
                  board_affiliation: {
                    type: "string",
                  },
                  board_contact: {
                    title: "Board Contact",
                    type: "object",
                    properties: {
                      phone: {
                        type: "string",
                      },
                      extension: {
                        type: "string",
                      },
                      email: {
                        type: "string",
                      },
                      address: {
                        type: "string",
                      },
                    },
                  },
                },
              },
            ],
          },
        },
      },
      data: {
        type: "object",
        properties: {
          data_monitoring_committee: {
            type: "string",
            enum: [],
            options: YES_NO_UNSELECTED,
          },
        },
      },
      fda: {
        type: "object",
        properties: {
          fda_regulated_intervention: {
            type: "string",
            enum: [],
            options: YES_NO_UNSELECTED,
          },
        },
        dependencies: {
          fda_regulated_intervention: {
            oneOf: [
              {
                properties: {
                  fda_regulated_intervention: {
                    enum: ["Yes"],
                  },
                  section_801_clinical_trial: {
                    type: "string",
                    enum: [],
                    options: YES_NO_UNSELECTED,
                  },
                },
              },
            ],
          },
        },
      },
    },
  },
  expanded_access: {
    title: "Oversight",
    type: "object",
    properties: {
      us_ind: {
        type: "object",
        properties: {
          us_fda_ind_ide: {
            type: "string",
            enum: [],
            options: YES_NO_UNSELECTED,
          },
        },
        dependencies: {
          us_fda_ind_ide: {
            oneOf: [
              {
                properties: {
                  us_fda_ind_ide: {
                    enum: ["Yes"],
                  },
                  fda_center: {
                    enum: ["CDER", "CBER", "CDRH"],
                  },
                  ind_ide_number: {
                    type: "string",
                  },
                  ind_serial_number: {
                    type: "string",
                  },
                },
              },
            ],
          },
        },
      },
    },
  },
}

export const contact_information = {
  interventional_observational: {
    type: "object",
    variant: "parent",
    properties: {
      responsible_party: {
        type: "string",
        enum: ["Sponsor", "Principal Investigator", "Sponsor-Investigator"],
      },
      sponsor: {
        type: "string",
        enum: [],
      },
      collaborators: {
        type: "string",
        enum: [],
      },
      central_contacts: {
        title: "Central Contact Person",
        type: "object",
        properties: {
          central_contacts_first_name: {
            type: "string",
          },
          central_contacts_middle_name: {
            type: "string",
          },
          central_contacts_last_name: {
            type: "string",
          },
          central_contacts_degree: {
            type: "string",
          },
          central_contacts_phone_number: {
            type: "string",
          },
          central_contacts_ext: {
            type: "string",
          },
          central_contacts_email: {
            type: "string",
          },
        },
      },
      central_contacts_backup: {
        title: "Central Contact Backup",
        type: "object",
        properties: {
          central_contacts_backup_first_name: {
            type: "string",
          },
          central_contacts_backup_middle_name: {
            type: "string",
          },
          central_contacts_backup_last_name: {
            type: "string",
          },
          central_contacts_backup_degree: {
            type: "string",
          },
          central_contacts_backup_phone_number: {
            type: "string",
          },
          central_contacts_backup_ext: {
            type: "string",
          },
          central_contacts_backup_email: {
            type: "string",
          },
        },
      },
      overall_study_officials: {
        title: "Overall Study Officials",
        type: "array",
        items: {
          type: "object",
          onAddButtonTitle: "Add Study Official",
          properties: {
            overall_study_officials_first_name: {
              type: "string",
            },
            overall_study_officials_middle_name: {
              type: "string",
            },
            overall_study_officials_last_name: {
              type: "string",
            },
            overall_study_officials_degree: {
              type: "string",
            },
            overall_study_officials_organization_affiliation: {
              type: "string",
            },
            official_role: {
              type: "string",
              enum: ["Study Chair", "Study Director", "Principal Investigator"],
            },
          },
        },
      },
      ...eupasProperties.contact_information,
    },
    dependencies: {
      responsible_party: {
        oneOf: [
          {
            properties: {
              responsible_party: {
                enum: ["Principal Investigator"],
              },
              investigator_name: {
                type: "string",
              },
              investigator_official_title: {
                type: "string",
              },
              investigator_affiliation: {
                type: "string",
              },
            },
          },
          {
            properties: {
              responsible_party: {
                enum: ["Sponsor-Investigator"],
              },
              investigator_name: {
                title: "Investigator Name[Username]",
                type: "string",
                placeholder: "--Select--",
                errorMessage: "Investigator Name has not been selected.",
              },
              investigator_official_title: {
                title: "Investigator Official Title",
                type: "string",
                placeholder: "Enter Official Title",
              },
              investigator_affiliation: {
                title: "Investigator Affiliation",
                type: "string",
                placeholder: "Enter Affiliation",
              },
            },
          },
          {
            properties: {
              responsible_party: {
                enum: ["Sponsor"],
              },
            },
          },
        ],
      },
      ...eupasDependencies.contact_information,
    },
  },
  expanded_access: {
    type: "object",
    variant: "parent",
    properties: {
      responsible_party: {
        type: "string",
        enum: ["Sponsor", "Principal Investigator", "Sponsor-Investigator"],
      },
      sponsor: {
        enum: [
          "--Select--",
          "Ariad Pharmaceuticals",
          "Baxalta now part of Shire",
          "Millennium Pharmaceuticals, Inc.",
          "Shire",
          "Takeda",
          "Tigenix S.A.U.",
        ],
      },
      collaborators: {
        type: "string",
        enum: [],
      },
      central_contacts: {
        title: "Central Contact Person",
        type: "object",
        properties: {
          central_contacts_first_name: {
            type: "string",
          },
          central_contacts_middle_name: {
            type: "string",
          },
          central_contacts_last_name: {
            type: "string",
          },
          central_contacts_degree: {
            type: "string",
          },
          central_contacts_phone_number: {
            type: "string",
          },
          central_contacts_ext: {
            type: "string",
          },
          central_contacts_email: {
            type: "string",
          },
        },
      },
      central_contacts_backup: {
        title: "Central Contact Backup",
        type: "object",
        properties: {
          central_contacts_backup_first_name: {
            type: "string",
          },
          central_contacts_backup_middle_name: {
            type: "string",
          },
          central_contacts_backup_last_name: {
            type: "string",
          },
          central_contacts_backup_degree: {
            type: "string",
          },
          central_contacts_backup_phone_number: {
            type: "string",
          },
          central_contacts_backup_ext: {
            type: "string",
          },
          central_contacts_backup_email: {
            type: "string",
          },
        },
      },
      overall_study_officials: {
        title: "Overall Study Officials",
        type: "array",
        items: {
          type: "object",
          onAddButtonTitle: "Add Study Official",
          properties: {
            overall_study_officials_first_name: {
              type: "string",
            },
            overall_study_officials_middle_name: {
              type: "string",
            },
            overall_study_officials_last_name: {
              type: "string",
            },
            overall_study_officials_degree: {
              type: "string",
            },
            overall_study_officials_organization_affiliation: {
              type: "string",
            },
            official_role: {
              type: "string",
              enum: ["Study Chair", "Study Director", "Principal Investigator"],
            },
          },
        },
      },
      ...eupasProperties.contact_information,
    },
    dependencies: {
      responsible_party: {
        oneOf: [
          {
            properties: {
              responsible_party: {
                enum: ["Principal Investigator"],
              },
              investigator_name: {
                type: "string",
              },
              investigator_official_title: {
                type: "string",
              },
              investigator_affiliation: {
                type: "string",
              },
            },
          },
          {
            properties: {
              responsible_party: {
                enum: ["Sponsor-Investigator"],
              },
              investigator_name: {
                title: "Investigator Name[Username]",
                type: "string",
                placeholder: "--Select--",
                errorMessage: "Investigator Name has not been selected.",
              },
              investigator_official_title: {
                title: "Investigator Official Title",
                type: "string",
                placeholder: "Enter Official Title",
              },
              investigator_affiliation: {
                title: "Investigator Affiliation",
                type: "string",
                placeholder: "Enter Affiliation",
              },
            },
          },
          {
            properties: {
              responsible_party: {
                enum: ["Sponsor"],
              },
            },
          },
        ],
      },
      ...eupasDependencies.contact_information,
    },
  },
}

export const study_outcome_measures = {
  interventional_observational: {
    title: "Study Endpoints / Outcome Measures",
    type: "object",
    properties: {
      outcome_measures: {
        type: "array",
        items: {
          type: "object",
          onAddButtonTitle: "Add Secondary Outcome",
          properties: {
            type: {
              type: "string",
              enum: ["Primary", "Secondary", "Other Pre-Specified"],
            },
            title: {
              type: "string",
            },
            time_frame: {
              type: "string",
            },
            description: {
              type: "string",
            },
          },
        },
      },
      outcome_measures_ctis: {
        type: "array",
        items: {
          type: "object",
          onAddButtonTitle: "Add Secondary Outcome",
          properties: {
            type: {
              type: "string",
              enum: ["Primary", "Secondary", "Other Pre-Specified"],
            },
            title: {
              type: "string",
            },
            ...ctisProperties.study_outcome_measures.outcome_measures_ctis.items,
          },
        },
      },
      ...eupasProperties.study_outcome_measures,
    },
  },
  expanded_access: {
    title: "Study Endpoints / Outcome Measures",
    type: "object",
    properties: {
      ...eupasProperties.study_outcome_measures,
    },
  },
}

export const study_timeline = {
  interventional_observational: {
    title: "Study Timelines and Status",
    type: "object",
    properties: {
      record_verification_date: {
        title: "Record Verification Date",
        type: "object",
        properties: {
          select_month: {
            type: "string",
            enum: [
              "January",
              "February",
              "March",
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
            ],
          },
          year: {
            type: "number",
          },
        },
      },
      recruitment: {
        type: "object",
        properties: {
          recruitment_status: {
            type: "string",
            enum: [],
            options: RECRUITMENT_STATUS_OPTIONS,
            valueMapping: RECRUITMENT_STATUS_MAPPING,
          },
        },
        dependencies: {
          recruitment_status: {
            oneOf: [
              {
                properties: {
                  recruitment_status: {
                    enum: ["Suspended"],
                    options: RECRUITMENT_STATUS_OPTIONS,
                    valueMapping: RECRUITMENT_STATUS_MAPPING,
                  },
                  study_stopped: {
                    type: "string",
                  },
                },
              },
              {
                properties: {
                  recruitment_status: {
                    enum: ["Terminated (Halted Prematurely)"],
                    options: RECRUITMENT_STATUS_OPTIONS,
                    valueMapping: RECRUITMENT_STATUS_MAPPING,
                  },
                  study_stopped: {
                    title: "Why Study Stopped?",
                    type: "string",
                    placeholder: "Enter Reason",
                  },
                },
              },
              {
                properties: {
                  recruitment_status: {
                    enum: ["Withdrawn (No Participants Enrolled)"],
                    options: RECRUITMENT_STATUS_OPTIONS,
                    valueMapping: RECRUITMENT_STATUS_MAPPING,
                  },
                  study_stopped: {
                    title: "Why Study Stopped?",
                    type: "string",
                    placeholder: "Enter Reason",
                  },
                },
              },
            ],
          },
        },
      },
      milestone_dates: {
        type: "object",
        properties: {
          ...ctisProperties.study_timeline.milestone_dates.properties,
        },
      },
      ...ctisProperties.study_timeline.properties,
      ...eupasProperties.study_timeline,
    },
  },
  expanded_access: {
    title: "Study Timelines and Status",
    type: "object",
    properties: {
      record_verification_date: {
        title: "Record Verification Date",
        type: "object",
        properties: {
          select_month: {
            type: "string",
            enum: [
              "January",
              "February",
              "March",
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
            ],
          },
          year: {
            type: "number",
          },
        },
      },
      expanded: {
        type: "object",
        properties: {
          expanded_status: {
            type: "string",
            enum: ["Available", "No Longer Available", "Temporarily Not Available", "Approved For Marketing"],
          },
        },
      },
      milestone_dates: {
        type: "object",
        properties: {
          ...ctisProperties.study_timeline.milestone_dates.properties,
        },
      },
      ...ctisProperties.study_timeline.properties,
      ...eupasProperties.study_timeline,
    },
  },
}

export const study_description = {
  type: "object",
  properties: {
    brief_summary: {
      type: "string",
      maxLength: 5000,
    },
    detailed_description: {
      type: "string",
      maxLength: 32000,
    },
    study_conditions: {
      type: "string",
      maxLength: 160,
      enum: [],
    },
    study_keywords: {
      type: "string",
      enum: null,
    },
    ...eupasProperties.study_description,
    ...ctisProperties.study_description,
  },
  dependencies: {
    ...eupasDependencies.study_description,
    ...ctisDependencies.study_description,
  },
}

export const study_scope = {
  interventional: {
    type: "object",
    properties: {
      primary_purpose: {
        type: "string",
        enum: [],
        options: PRIMARY_PURPOSE,
        valueMapping: PRIMARY_PURPOSE_MAPPING,
      },
      study_phase: {
        type: "string",
        enum: [],
        options: STUDY_PHASES,
      },
      interventional_study_model: {
        type: "string",
        enum: [],
        options: INTERVENTIONAL_STUDY_MODEL,
      },
      model_description: {
        type: "string",
        maxLength: 1000,
      },
      masking: {
        type: "array",
        uniqueItems: true,
        items: {
          type: "string",
          enum: ["Participant", "Care Provider", "Investigator", "Outcomes Assessor"],
          errorMessage: "Masking has not been entered",
        },
      },
      number_of_arms: {
        type: "string",
      },
      masking_description: {
        type: "string",
        maxLength: 1000,
      },
      allocation: {
        type: "string",
        enum: [],
        options: ALLOCATION,
      },
      enrollment_type: {
        type: "string",
        enum: [],
        options: DATE_TYPE,
      },
      enrollment_number: {
        type: "string",
      },
      ...eupasProperties.study_scope.interventional,
      ...eupasProperties.study_scope.shared,
      ...ctisProperties.study_scope,
    },
    dependencies: {
      // ...eupasDependencies.study_scope,
    },
  },
  observational: {
    type: "object",
    properties: {
      patient_registry: {
        type: "array",
        uniqueItems: true,
        items: {
          type: "string",
          enum: ["Patient Registry"],
        },
      },
      observational_study_model: {
        type: "string",
        enum: [],
        options: OBSERVATIONAL_STUDY_MODEL,
        valueMapping: OBSERVATIONAL_STUDY_MODEL_MAPPING,
      },
      time_perspective: {
        type: "string",
        enum: ["Retrospective", "Prospective", "Cross-Sectional", "Other"],
      },
      biospecimen: {
        type: "object",
        properties: {
          biospeciment_retention: {
            type: "string",
            enum: ["None Retained", "Samples with DNA", "Samples without DNA"],
          },
        },
        dependencies: {
          biospeciment_retention: {
            oneOf: [
              {
                properties: {
                  biospeciment_retention: {
                    enum: ["Samples with DNA", "Samples without DNA"],
                  },
                  biospecimen_description: {
                    type: "string",
                  },
                },
              },
            ],
          },
        },
      },
      enrollment_type: {
        type: "string",
        enum: [],
        options: DATE_TYPE,
      },
      enrollment_number: {
        type: "string",
      },
      number_of_cohorts: {
        type: "string",
      },
      ...eupasProperties.study_scope.observational,
      ...eupasProperties.study_scope.shared,
      ...ctisProperties.study_scope,
    },
    dependencies: {
      patient_registry: {
        oneOf: [
          {
            properties: {
              patient_registry: {
                items: {
                  enum: ["Patient Registry"],
                },
              },
              follow_up_duration: {
                type: "string",
              },
              duration_type: {
                enum: ["Years", "Months", "Weeks", "Days"],
              },
              additional_patient_registry_data: {
                type: "string",
              },
            },
          },
        ],
      },
      // ...eupasDependencies.study_scope,
    },
  },
  expanded_access: {
    type: "object",
    properties: {
      selection: {
        type: "array",
        uniqueItems: true,
        items: {
          type: "string",
          enum: ["Individual Patients", "Intermediate-size Population", "Treatment IND/Protocol"],
          errorMessage: "Results may not be specified for Expanded Access records.",
        },
      },
      enrollment_number: {
        type: "string",
      },
      ...eupasProperties.study_scope.shared,
      ...ctisProperties.study_scope,
    },
    dependencies: {
      // ...eupasDependencies.study_scope,
    },
  },
}

export const study_arms = {
  interventional: {
    title: "Arms and Interventions",
    type: "object",
    properties: {
      arms: {
        title: "Arms",
        type: "string",
        addButtonTitle: "Add Arm",
        properties: {
          title: {
            title: "Arm Title",
            type: "textarea",
            tooltip: "Formerly Arm Label. Brief, descriptive label to be used as row or column heading in tables.",
            charCountEnabled: true,
            maxChars: 100,
          },
          type: {
            title: "Arm Type",
            type: "dropdown",
            enum: ARM_TYPES,
          },
          description: {
            title: "Arm Description",
            type: "textarea",
            tooltip:
              "Describe the intervention(s) to be administered. For drugs use generic name and include dosage form, dosage, frequency and duration.",
            charCountEnabled: true,
            maxChars: 999,
          },
        },
      },
      interventions: {
        title: "Interventions",
        type: "string",
        addButtonTitle: "Add Intervention",
        properties: {
          type: {
            title: "Intervention Type",
            type: "dropdown",
            enum: [
              "Drug",
              "Device",
              "Biological/Vaccine",
              "Procedure/Surgery",
              "Radiation",
              "Behavioral",
              "Genetic",
              "Dietary Supplement",
              "Combination Product",
              "Diagnostic Test",
              "Other",
            ],
          },
          name: {
            title: "Intervention Name",
            type: "textarea",
            tooltip:
              "For a drug, use generic name if established. Use the same name as in the associated Arm/Group Description(s).",
            charCountEnabled: true,
            maxChars: 200,
          },
          other_intervention_names: {
            title: "Other Intervention Names",
            type: "tags",
            tooltip:
              "Include brand names, serial numbers and code names to improve search results on the ClinicalTrials.gov web site.\n\nNote: The character count includes ', ' between each item.",
            charCountEnabled: true,
            maxChars: 200,
          },
          description: {
            title: "Intervention Description",
            type: "textarea",
            tooltip: "Do not repeat information already included in arm/group descriptions.",
            charCountEnabled: true,
            maxChars: 1000,
          },
        },
      },
      crossref: {
        type: "string",
        title: "Cross Reference",
      },
      ...eupasProperties.study_arms.interventional,
      ...eupasProperties.study_arms.shared,
      ...ctisProperties.study_arms,
    },
    dependencies: {
      ...eupasDependencies.study_arms,
      ...ctisDependencies.study_arms,
    },
  },
  observational: {
    title: "Arms and Interventions",
    type: "object",
    properties: {
      arms: {
        title: "Groups/Cohorts",
        type: "string",
        addButtonTitle: "Add Group",
        properties: {
          title: {
            title: "Group/Cohort Label",
            type: "string",
            charCountEnabled: true,
            maxChars: 100,
          },
          description: {
            title: "Group/Cohort Description",
            type: "textarea",
            charCountEnabled: true,
            maxChars: 999,
          },
        },
      },
      interventions: {
        title: "Interventions",
        type: "string",
        addButtonTitle: "Add Intervention",
        properties: {
          type: {
            title: "Intervention Type",
            type: "dropdown",
            enum: [
              "Drug",
              "Device",
              "Biological/Vaccine",
              "Procedure/Surgery",
              "Radiation",
              "Behavioral",
              "Genetic",
              "Dietary Supplement",
              "Combination Product",
              "Diagnostic Test",
              "Other",
            ],
          },
          name: {
            title: "Intervention Name",
            type: "string",
            charCountEnabled: true,
            maxChars: 200,
          },
          other_intervention_names: {
            title: "Other Intervention Names",
            type: "tags",
            tooltip:
              "Include brand names, serial numbers and code names to improve search results on the ClinicalTrials.gov web site.\n\nNote: The character count includes ', ' between each item.",
            charCountEnabled: true,
            maxChars: 200,
          },
          description: {
            title: "Intervention Description",
            type: "textarea",
            charCountEnabled: true,
            maxChars: 1000,
          },
        },
      },
      crossref: {
        type: "string",
        title: "Cross Reference",
      },
      ...eupasProperties.study_arms.shared,
      ...ctisProperties.study_arms,
    },
    dependencies: {
      ...eupasDependencies.study_arms,
      ...ctisDependencies.study_arms,
    },
  },
  expanded_access: {
    title: "Arms and Interventions",
    type: "object",
    properties: {
      interventions: {
        title: "Interventions",
        type: "string",
        addButtonTitle: "Add Intervention",
        properties: {
          type: {
            title: "Intervention Type",
            type: "dropdown",
            enum: [
              "Drug",
              "Device",
              "Biological/Vaccine",
              "Procedure/Surgery",
              "Radiation",
              "Behavioral",
              "Genetic",
              "Dietary Supplement",
              "Combination Product",
              "Diagnostic Test",
              "Other",
            ],
          },
          name: {
            title: "Intervention Name",
            type: "string",
          },
          other_intervention_names: {
            title: "Other Intervention Names",
            type: "tags",
            tooltip:
              "Include brand names, serial numbers and code names to improve search results on the ClinicalTrials.gov web site.\n\nNote: The character count includes ', ' between each item.",
          },
          description: {
            title: "Intervention Description",
            type: "textarea",
          },
        },
      },
      ...eupasProperties.study_arms.shared,
    },
    dependencies: {
      ...eupasDependencies.study_arms,
    },
  },
}

export const study_eligibilty = {
  interventional: {
    title: "Eligibility",
    type: "object",
    properties: {
      sex: {
        type: "string",
        enum: [],
        options: GENDER,
      },
      gender_based: {
        title: "Gender Based",
        type: "object",
        properties: {
          gender_based_1: {
            enum: [],
            options: YES_NO_UNSELECTED,
          },
        },
        dependencies: {
          gender_based_1: {
            oneOf: [
              {
                properties: {
                  gender_based_1: {
                    enum: ["Yes"],
                  },
                  gender_eligibility_description: {
                    type: "string",
                  },
                },
              },
              {
                properties: {
                  gender_based_1: {
                    enum: ["No"],
                  },
                },
              },
            ],
          },
        },
      },
      age_limits: ageLimits,
      accept_healthy_volunteers: {
        type: "string",
        enum: [],
        options: YES_NO_UNSELECTED,
      },
      is_clinical_study: {
        type: "string",
        enum: ["Yes", "No", "NA"],
      },
      eligibility_criteria: {
        type: "string",
      },
      ...eupasProperties.study_eligibilty,
      ...ctisProperties.study_eligibilty,
    },
    dependencies: {
      ...eupasDependencies.study_eligibilty,
    },
  },
  observational: {
    title: "Eligibility",
    type: "object",
    properties: {
      sex: {
        type: "string",
        enum: null,
        options: GENDER,
      },
      gender_based: {
        title: "Gender Based",
        type: "object",
        properties: {
          gender_based_1: {
            enum: [],
            options: YES_NO_UNSELECTED,
          },
        },
        dependencies: {
          gender_based_1: {
            oneOf: [
              {
                properties: {
                  gender_based_1: {
                    enum: ["Yes"],
                  },
                  gender_eligibility_description: {
                    type: "string",
                  },
                },
              },
            ],
          },
        },
      },
      age_limits: ageLimits,
      accept_healthy_volunteers: {
        type: "string",
        enum: [],
        options: YES_NO_UNSELECTED,
      },
      eligibility_criteria: {
        type: "string",
      },
      study_population_description: {
        type: "string",
      },
      sampling_method: {
        type: "string",
        enum: ["Probability Sample", "Non-Probability Sample"],
      },
      ...eupasProperties.study_eligibilty,
      ...ctisProperties.study_eligibilty,
    },
    dependencies: {
      ...eupasDependencies.study_eligibilty,
    },
  },
  expanded_access: {
    title: "Eligibility",
    type: "object",
    properties: {
      sex: {
        type: "string",
        enum: [],
        options: GENDER,
      },
      gender_based: {
        title: "Gender Based",
        type: "object",
        properties: {
          gender_based_1: {
            enum: [],
            options: YES_NO_UNSELECTED,
          },
        },
        dependencies: {
          gender_based_1: {
            oneOf: [
              {
                properties: {
                  gender_based_1: {
                    enum: ["Yes"],
                  },
                  gender_eligibility_description: {
                    type: "string",
                  },
                },
              },
            ],
          },
        },
      },
      age_limits: ageLimits,
      eligibility_criteria: {
        type: "string",
      },
      ...eupasProperties.study_eligibilty,
      ...ctisProperties.study_eligibilty,
    },
    dependencies: {
      ...eupasDependencies.study_eligibilty,
    },
  },
}

export const study_ipd = {
  interventional_observational: {
    title: "IPD Sharing Statements",
    type: "object",
    properties: {
      plan_to_share_IPD: {
        type: "string",
        enum: [],
        options: SHARE_IPD,
      },
    },
    dependencies: {
      plan_to_share_IPD: {
        oneOf: [
          {
            properties: {
              plan_to_share_IPD: {
                enum: ["Yes"],
              },
              plan_description: {
                type: "string",
                maxLength: 1000,
              },
              ipd_sharing_info: {
                type: "array",
                uniqueItems: true,
                items: {
                  type: "string",
                  enum: [
                    "Study Protocol",
                    "Statistical Analysis Plan (SAP)",
                    "Informed Consent Form (ICF)",
                    "Clinical Study Report (CSR)",
                    "Analytic Code",
                  ],
                },
              },
              time_frame: {
                type: "string",
                maxLength: 1000,
              },
              url_text: {
                type: "string",
                maxLength: 3999,
              },
              access_criteria: {
                type: "string",
                maxLength: 1000,
              },
            },
          },
          {
            properties: {
              plan_to_share_IPD: {
                enum: ["No"],
              },
              plan_description: {
                title: "IPD Sharing Plan Description",
                type: "string",
                maxLength: 1000,
                errorMessage: "IPD Description must have no more than 1000 characters",
              },
            },
          },
          {
            properties: {
              plan_to_share_IPD: {
                enum: ["Undecided"],
              },
              plan_description: {
                title: "IPD Sharing Plan Description",
                type: "string",
                maxLength: 1000,
                errorMessage: "IPD Description must have no more than 1000 characters",
              },
            },
          },
        ],
      },
    },
  },
}

export const study_reference = {
  interventional: {
    title: "Citations, References and URLs",
    type: "object",
    properties: {
      citations: {
        type: "array",
        items: {
          type: "object",
          onAddButtonTitle: "Add Citation",
          properties: {
            pub_med_id: {
              type: "string",
              maxLength: 8,
            },
            result_reference: {
              type: "string",
              enum: [],
              options: YES_NO_UNSELECTED,
            },
            citation_text: {
              type: "string",
              maxLength: 2000,
            },
          },
        },
      },
      links: {
        type: "array",
        items: {
          type: "object",
          onAddButtonTitle: "Add Link",
          properties: {
            url_text: {
              type: "string",
              maxLength: 3999,
            },
            description_text: {
              type: "string",
              maxLength: 254,
            },
          },
        },
      },
      available: {
        type: "array",
        items: {
          type: "object",
          onAddButtonTitle: "Add Data/Information",
          properties: {
            data_info: {
              type: "string",
              enum: DATA_INFO,
            },
            url_text: {
              type: "string",
              maxLength: 3999,
            },
            identifier: {
              type: "string",
              maxLength: 30,
            },
            comments: {
              type: "string",
              maxLength: 2000,
            },
          },
          dependencies: {
            data_info: {
              oneOf: [
                {
                  properties: {
                    data_info: {
                      enum: ["Other"],
                    },
                    description: {
                      type: "string",
                    },
                  },
                },
              ],
            },
          },
        },
      },
      ...eupasProperties.study_reference,
    },
  },
  observational: {
    title: "Citations, References and URLs",
    type: "object",
    properties: {
      citations: {
        type: "array",
        items: {
          type: "object",
          onAddButtonTitle: "Add Citation",
          properties: {
            pub_med_id: {
              type: "string",
              maxLength: 8,
            },
            result_reference: {
              type: "string",
              enum: [],
              options: YES_NO_UNSELECTED,
            },
            citation_text: {
              type: "string",
              maxLength: 2000,
            },
          },
        },
      },
      links: {
        type: "array",
        items: {
          type: "object",
          onAddButtonTitle: "Add Link",
          properties: {
            url_text: {
              type: "string",
              maxLength: 3999,
            },
            description_text: {
              type: "string",
              maxLength: 254,
            },
          },
        },
      },
      available: {
        type: "array",
        items: {
          type: "object",
          onAddButtonTitle: "Add Data/Information",
          properties: {
            data_info: {
              type: "string",
              enum: DATA_INFO,
            },
            url_text: {
              type: "string",
              maxLength: 3999,
            },
            identifier: {
              type: "string",
              maxLength: 30,
            },
            comments: {
              type: "string",
              maxLength: 2000,
            },
          },
          dependencies: {
            data_info: {
              oneOf: [
                {
                  properties: {
                    data_info: {
                      enum: ["Other"],
                    },
                    description: {
                      type: "string",
                    },
                  },
                },
              ],
            },
          },
        },
      },
      ...eupasProperties.study_reference,
    },
  },
  expanded_access: {
    title: "Citations, References and URLs",
    type: "object",
    properties: {
      citations: {
        type: "array",
        items: {
          type: "object",
          onAddButtonTitle: "Add Citation",
          properties: {
            pub_med_id: {
              type: "string",
              maxLength: 8,
            },
            result_reference: {
              type: "string",
              enum: [],
              options: YES_NO_UNSELECTED,
            },
            citation_text: {
              type: "string",
              maxLength: 2000,
            },
          },
        },
      },
      links: {
        type: "array",
        items: {
          type: "object",
          onAddButtonTitle: "Add Link",
          properties: {
            url_text: {
              type: "string",
              maxLength: 3999,
            },
            description_text: {
              type: "string",
              maxLength: 254,
            },
          },
        },
      },
      available: {
        type: "array",
        items: {
          type: "object",
          onAddButtonTitle: "Add Data/Information",
          properties: {
            data_info: {
              type: "string",
              enum: DATA_INFO,
            },
            url_text: {
              type: "string",
              maxLength: 3999,
            },
            identifier: {
              type: "string",
              maxLength: 30,
            },
            comments: {
              type: "string",
              maxLength: 2000,
            },
          },
          dependencies: {
            data_info: {
              oneOf: [
                {
                  properties: {
                    data_info: {
                      enum: ["Other"],
                    },
                    description: {
                      type: "string",
                    },
                  },
                },
              ],
            },
          },
        },
      },
      ...eupasProperties.study_reference,
    },
  },
}

export const ctis_form = {
  type: "object",
  variant: "parent",
  properties: {
    ...ctisProperties.ctis_form,
  },
}

export const member_states_concerned = {
  type: "object",
  variant: "parent",
  properties: {
    member_states_concerned: ctisProperties.member_states_concerned,
  },
}
